/**
 * 券「使用」跳转：按 applicableScenes + sceneRuntime.couponUrl，逻辑对齐活动任务 jumpUrls（RewardHub）
 */

import { Platform, Bridge, Util } from "@xt/xtbase";
import {
  COUPON_SCENE_AI_COPYTRADE,
  parseUserCouponApplicableScenes,
} from "./normalizeUserCoupon";
import { couponUrlHasJumpTarget, resolveVoucherSceneCouponUrl } from "./voucherRules";

const { getUrl } = Util;

function isPwaStandalone() {
  if (typeof window === "undefined") return false;
  try {
    if (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) return true;
    if (window.navigator && window.navigator.standalone) return true;
  } catch {
    /* ignore */
  }
  return false;
}

function shouldUseAppRouteForVoucherJump() {
  return Platform.isInApp() && !isPwaStandalone();
}

/** 与 RewardHub resolveTaskNativeJumpPath 一致 */
function resolveCouponUrlNativePath(couponUrl) {
  if (!couponUrl || typeof couponUrl !== "object") return "";
  const ios = typeof couponUrl.ios === "string" ? couponUrl.ios.trim() : "";
  const android = typeof couponUrl.android === "string" ? couponUrl.android.trim() : "";
  if (Platform.isIOS()) return ios || android;
  if (Platform.isAndroid()) return android || ios;
  return android || ios;
}

/** 与 RewardHub normalizeAppRoutePath 一致 */
function normalizeAppRoutePath(raw) {
  const s = (raw || "").trim();
  if (!s) return "";
  if (/^https?:\/\//i.test(s)) {
    try {
      const u = new URL(s);
      return `${u.pathname}${u.search}${u.hash}`;
    } catch {
      return s;
    }
  }
  if (!s.startsWith("/")) return `/${s.replace(/^\/+/, "")}`;
  return s;
}

/** 与 RewardHub resolveTaskJumpUrl 一致（couponUrl 当作 jumpUrls） */
function resolveCouponUrlWebPath(couponUrl) {
  if (!couponUrl) return "";
  if (typeof couponUrl === "string" && couponUrl.trim()) return couponUrl.trim();
  if (typeof couponUrl !== "object") return "";
  const preferKeys = ["web", "WEB", "Web", "h5", "H5", "mobile", "MOBILE", "common", "COMMON"];
  for (const k of preferKeys) {
    const v = couponUrl[k];
    if (typeof v === "string" && v.trim()) return v.trim();
    if (v && typeof v === "object" && typeof v.url === "string" && v.url.trim()) return v.url.trim();
  }
  for (const v of Object.values(couponUrl)) {
    if (typeof v === "string" && v.trim()) return v.trim();
    if (v && typeof v === "object" && typeof v.url === "string" && v.url.trim()) return v.url.trim();
  }
  return "";
}

function getItemApplicableScenes(item) {
  if (Array.isArray(item?.applicableScenesList) && item.applicableScenesList.length) {
    return item.applicableScenesList.map((s) => String(s).trim().toUpperCase()).filter(Boolean);
  }
  const raw = item?.raw && typeof item.raw === "object" ? item.raw : item;
  return parseUserCouponApplicableScenes(raw);
}

/** 是否展示「使用」：AVAILABLE 且（任一场景配置了 couponUrl，或 AI 跟单走历史 aicopy 兜底） */
export function canShowVoucherUse(item, showUse = true) {
  if (!showUse) return false;
  if (String(item?.status || "").toUpperCase() !== "AVAILABLE") return false;
  const raw = item?.raw && typeof item.raw === "object" ? item.raw : item;
  const couponUrl = resolveVoucherSceneCouponUrl(raw);
  if (couponUrlHasJumpTarget(couponUrl)) return true;
  return getItemApplicableScenes(item).includes(COUPON_SCENE_AI_COPYTRADE);
}

/** @deprecated 使用 canShowVoucherUse */
export const canShowVoucherAicopyUse = canShowVoucherUse;

/** 无 couponUrl 时 AI 跟单场景兜底 URL */
export function buildVoucherAicopyUseUrl(item) {
  const qs = new URLSearchParams({ payWithVoucher: "1" });
  if (item?.id) qs.set("userCouponId", String(item.id));
  return getUrl(`/aicopy?${qs.toString()}`);
}

function navigateWebPath(rawPath, router) {
  const url = (rawPath || "").trim();
  if (!url) return false;
  if (/^https?:\/\//i.test(url)) {
    window.location.href = url;
    return true;
  }
  const path = url.startsWith("/") ? url : `/${url}`;
  const target = getUrl(path);
  if (router?.push) {
    router.push(target);
    return true;
  }
  window.location.href = target;
  return true;
}

function navigateFallback(item, router) {
  if (shouldUseAppRouteForVoucherJump()) {
    Bridge.call("appRoute", { path: "/aicopy", params: "" });
    return;
  }
  const target = buildVoucherAicopyUseUrl(item);
  if (router?.push) {
    router.push(target);
    return;
  }
  window.location.href = target;
}

/**
 * 券使用跳转：App 内有 ios/android 走 appRoute；否则 web H5；均未配置则 aicopy 兜底
 * @param {object} item — normalizeUserCoupon 结果（含 raw、id）
 * @param {{ router?: import('next/router').NextRouter }} [options]
 */
export function navigateVoucherUse(item, { router } = {}) {
  const raw = item?.raw && typeof item.raw === "object" ? item.raw : item;
  const couponUrl = resolveVoucherSceneCouponUrl(raw);

  if (couponUrl) {
    if (shouldUseAppRouteForVoucherJump()) {
      const nativeRaw = resolveCouponUrlNativePath(couponUrl);
      if (nativeRaw) {
        const appPath = normalizeAppRoutePath(nativeRaw);
        if (appPath) {
          Bridge.call("appRoute", { path: appPath, params: "" });
          return;
        }
      }
    }

    const webPath = resolveCouponUrlWebPath(couponUrl);
    if (webPath && navigateWebPath(webPath, router)) {
      return;
    }
  }

  if (getItemApplicableScenes(item).includes(COUPON_SCENE_AI_COPYTRADE)) {
    navigateFallback(item, router);
  }
}
